import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Card,
  Button,
  Stack,
  Divider,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Chip,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import HomeIcon from "@mui/icons-material/Home";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DaalchiniLogo from "../../assets/dalchini_logo.png";
import { useNavigate, useLocation } from "react-router-dom";

export default function Digital_Details({ claimedItem: propClaimedItem, claimResponse: propClaimResponse, orderId: propOrderId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [tncOpen, setTncOpen] = useState(false);
  const [showCode, setShowCode] = useState(false);
  
  const params = new URLSearchParams(location.search);
  const urlStatus = params.get("orderId");
  
  const state = location.state || {};
  const responseData = propClaimResponse || state.claimResponse;
  const claimedItem = propClaimedItem || state.claimedItem;
  
  const status = urlStatus || "success";
  
  // Check fulfilment type
  const fulfilmentType = responseData?.fulfilmentType || claimedItem?.fulfilment_type || "SUBSCRIPTION";
  const isVoucher = fulfilmentType === "VOUCHER";
  const isDGC = fulfilmentType === "DGC";
  const isSubscription = fulfilmentType === "SUBSCRIPTION";
  
  const handleBack = () => navigate(-1);
  const handleHome = () => navigate("/");

  const handleCopyCode = () => {
    if (responseData?.code) {
      navigator.clipboard.writeText(responseData.code);
      alert("Code copied to clipboard!");
    } else {
      navigator.clipboard.writeText("H28130Y5");
      alert("Code copied to clipboard!");
    }
  };

  const handleToggleCode = () => {
    setShowCode(!showCode);
  };

  // Updated handleGoToProvider based on fulfilmentType
  const handleGoToProvider = () => {
    const redemptionUrl = responseData?.redemptionUrl;
    const uiRedirect = responseData?.uiRedirect;
    
    // Only for DGC type, check GIFT_CARD_SCREEN
    if (isDGC) {
      navigate("/giftcard");
      return;
    }
    
    // For all other cases (VOUCHER, SUBSCRIPTION, or DGC without GIFT_CARD_SCREEN)
    // follow redemptionUrl
    if (redemptionUrl) {
      window.open(redemptionUrl, "_blank");
    } else {
      // Fallback for SUBSCRIPTION without redemptionUrl
      if (isSubscription) {
        const itemName = getItemName().toLowerCase();
        if (itemName.includes("netflix")) {
          window.open("https://www.netflix.com", "_blank");
        } else if (itemName.includes("healthify")) {
          window.open("https://www.healthify.me", "_blank");
        } else if (itemName.includes("onsurity")) {
          window.open("https://www.onsurity.com", "_blank");
        } else if (itemName.includes("amazon") || itemName.includes("prime")) {
          window.open("https://www.amazon.in", "_blank");
        } else {
          alert("Redirecting to provider website");
        }
      } else {
        alert("No redemption URL available");
      }
    }
  };

  const handleClaimReward = () => {
    alert("Claiming cashback!");
  };

  const getItemName = () => responseData?.variantName || claimedItem?.itemName || "Onsurity";
  const getItemDescription = () => responseData?.variantDescription || claimedItem?.itemDescription || "Cost-effective healthcare membership";
  
  // Get button text based on fulfilmentType and uiRedirect
  const getButtonText = () => {
    // For DGC with GIFT_CARD_SCREEN, show "View Gift Card"
    if (isDGC && responseData?.uiRedirect === "GIFT_CARD_SCREEN") {
      return "View Gift Card";
    }
    
    // Use ctaText if available
    if (responseData?.ctaText) {
      return responseData.ctaText;
    }
    
    // Fallback for subscription
    const name = getItemName().toLowerCase();
    if (name.includes("netflix")) return "Go To Netflix";
    if (name.includes("onsurity")) return "Go To Onsurity";
    if (name.includes("healthify")) return "Go To Healthify";
    if (name.includes("amazon") || name.includes("prime")) return "Go To Amazon Prime";
    return "Go To Provider";
  };
  
  const getProviderLogo = () => {
    const name = getItemName().toLowerCase();
    if (name.includes("netflix")) return "NETFLIX";
    if (name.includes("onsurity")) return "ONSURITY";
    if (name.includes("healthify")) return "HEALTHIFY";
    if (name.includes("amazon") || name.includes("prime")) return "PRIME";
    return getItemName().substring(0, 8).toUpperCase();
  };
  
  const getPlanDuration = () => {
    const days = responseData?.validityDays || claimedItem?.validityDays;
    if (days === 365) return "12 Months";
    if (days === 180) return "6 Months";
    if (days === 90) return "3 Months";
    if (days === 30) return "1 Month";
    return "3 Months";
  };
  
  const getValidUntil = () => {
    const days = responseData?.validityDays || claimedItem?.validityDays || 90;
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };
  
  const getPurchasedWith = () => responseData?.email || localStorage.getItem("userEmail") || "amisha01@gmail.com";
  const getCashbackAmount = () => claimedItem?.offerPrice || 0;
  const hasReward = () => getCashbackAmount() > 0;
  const getCode = () => responseData?.code || "H28130Y5";
  
  const getImageUrl = () => {
    if (responseData?.variantImageUrl) {
      return `${process.env.REACT_APP_IMAGE_BASE_URL}/${responseData.variantImageUrl}`;
    }
    if (claimedItem?.imageUrl) {
      return `${process.env.REACT_APP_IMAGE_BASE_URL}/${claimedItem.imageUrl}`;
    }
    return null;
  };
  
  const getTncList = () => {
    if (responseData?.tnc) {
      return [responseData.tnc];
    }
    return [
      "This voucher is valid for premium subscription only.",
      "Applicable for new users or first-time premium upgrades.",
      "Voucher is non-transferable and cannot be resold.",
      "Cannot be combined with other offers, discounts, or promotions.",
      "Voucher must be redeemed before the expiry date.",
      "No cashback or refund will be provided for unused vouchers.",
      "In case of cancellation, the voucher will be considered used."
    ];
  };

  const Header = () => (
    <Stack direction="row" alignItems="center" justifyContent="center" sx={{ mb: 2, position: "relative" }}>
      <IconButton size="small" onClick={handleBack} sx={{ position: "absolute", left: 0 }}>
        <ArrowBackIosNewIcon fontSize="small" />
      </IconButton>
      <Box sx={{ textAlign: "center" }}>
        <img src={DaalchiniLogo} style={{ width: 80 }} alt="logo" />
      </Box>
    </Stack>
  );

  // ==================== VOUCHER UI (Only VOUCHER shows Get Code Section) ====================
  if (status === "success" && isVoucher) {
    const imageUrl = getImageUrl();
    const code = getCode();
    const displayCode = showCode ? code : "••••••••";
    const tncList = getTncList();
    
    return (
      <Box sx={{ maxWidth: 420, mx: "auto", minHeight: "100vh", bgcolor: "#F4F6F8", p: 2 }}>
        <Header />

        {/* Success Message */}
        <Box textAlign="center" sx={{ mt: 2, mb: 3 }}>
          {imageUrl && (
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                overflow: "hidden",
                mx: "auto",
                mb: 2,
                border: "2px solid #12B76A",
              }}
            >
              <img
                src={imageUrl}
                alt={getItemName()}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </Box>
          )}
          <Typography sx={{ fontWeight: 700, fontSize: 22 }}>
            Successfully Claimed!
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#6B7280", mt: 0.5 }}>
            Enjoy Your {getItemName()}
          </Typography>
        </Box>

        {/* Get Code Section - ONLY FOR VOUCHER */}
        <Box 
          sx={{ 
            display: "flex", 
            flexDirection: "row", 
            justifyContent: "space-between", 
            alignItems: "center", 
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            borderRadius: 2,
            px: 2,
            py: 1,
            mb: 3,
            bgcolor: "white"
          }}
        >
          <Typography 
            sx={{ 
              fontWeight: 500, 
              fontSize: 16, 
              letterSpacing: 2,
              fontFamily: "monospace",
              color: showCode ? "#1F2937" : "#9CA3AF"
            }}
          >
            {displayCode}
          </Typography>
          
          {!showCode ? (
            <Button
              sx={{
                color: "#12B76A",
                textTransform: "none",
                fontWeight: 600,
                minWidth: "auto",
                p: 0,
                "&:hover": { bgcolor: "transparent", color: "#0EA861" },
              }}
              onClick={handleToggleCode}
            >
              Get Code
            </Button>
          ) : (
            <IconButton
              sx={{
                color: "#12B76A",
                p: 0,
                "&:hover": { bgcolor: "transparent", color: "#0EA861" },
              }}
              onClick={handleCopyCode}
              size="small"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Plan Details Card */}
        <Card sx={{ borderRadius: 3, p: 2.5, mb: 2 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#6B7280", mb: 1 }}>
            Plan Details
          </Typography>

          <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 0.5 }}>
            {getItemName()} ({getPlanDuration()})
          </Typography>

          <Typography sx={{ fontSize: 14, color: "#6B7280", mb: 2 }}>
            {getItemDescription()}
          </Typography>

          <Divider sx={{ my: 1.5 }} />

          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                Purchased With
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {getPurchasedWith()}
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                Valid Until
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {getValidUntil()}
              </Typography>
            </Box>

            {/* Terms & Condition - Collapsible with bullet list */}
            <Box>
              <Button
                onClick={() => setTncOpen(!tncOpen)}
                sx={{
                  justifyContent: "space-between",
                  color: "#6B7280",
                  fontWeight: 500,
                  fontSize: 14,
                  textTransform: "none",
                  p: 0,
                  minWidth: "auto",
                  display: "flex",
                  width: "100%",
                }}
              >
                <span>Terms & Condition</span>
                {tncOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </Button>
              <Collapse in={tncOpen}>
                <Box sx={{ mt: 2 }}>
                  <List sx={{ listStyleType: 'disc', pl: 2 }}>
                    {tncList.map((item, index) => (
                      <ListItem 
                        key={index} 
                        sx={{ 
                          display: 'list-item', 
                          pl: 0, 
                          py: 0.5,
                          '&::marker': {
                            color: '#6B7280',
                            fontSize: 12,
                          }
                        }}
                      >
                        <ListItemText 
                          primary={item} 
                          sx={{ 
                            '& .MuiListItemText-primary': {
                              fontSize: 12,
                              color: '#6B7280',
                              lineHeight: 1.5,
                            }
                          }} 
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Collapse>
            </Box>
          </Stack>
        </Card>

        {/* Reward Unlocked Box */}
        {hasReward() && (
          <Card sx={{ borderRadius: 3, p: 2, mb: 3, bgcolor: "#EAF7F1", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: 16 }}>
                Reward Unlocked!
              </Typography>
              <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1.5 }}>
                You've Won Cashback On This Order
              </Typography>
              <Button
                variant="contained"
                size="small"
                sx={{
                  bgcolor: "#12B76A",
                  textTransform: "none",
                  borderRadius: 2,
                  px: 3,
                  "&:hover": { bgcolor: "#0EA861" },
                }}
                onClick={handleClaimReward}
              >
                Claim Now →
              </Button>
            </Box>
          </Card>
        )}

        {/* Go To Provider Button */}
        <Button
          fullWidth
          variant="contained"
          sx={{
            bgcolor: "#12B76A",
            borderRadius: 3,
            py: 1.6,
            fontWeight: 700,
            textTransform: "none",
            mb: 2,
            "&:hover": { bgcolor: "#0EA861" },
          }}
          onClick={handleGoToProvider}
        >
          {getButtonText()}
        </Button>

        {/* Home Button */}
        <Button
          fullWidth
          variant="outlined"
          sx={{
            borderRadius: 3,
            py: 1.6,
            fontWeight: 600,
            textTransform: "none",
            color: "#404237",
            borderColor: "#E5E7EB",
            "&:hover": {
              borderColor: "#14AE78",
              color: "#14AE78",
            },
          }}
          onClick={handleHome}
        >
          Home
        </Button>
      </Box>
    );
  }

  // ==================== DGC or SUBSCRIPTION UI (No Get Code Section) ====================
  if (status === "success" && (isDGC || isSubscription)) {
    const imageUrl = getImageUrl();
    
    return (
      <Box sx={{ maxWidth: 420, mx: "auto", minHeight: "100vh", bgcolor: "#F4F6F8", p: 2 }}>
        <Header />

        {/* Success Message */}
        <Box textAlign="center" sx={{ mt: 2, mb: 3 }}>
          {imageUrl && (
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                overflow: "hidden",
                mx: "auto",
                mb: 2,
                border: "2px solid #12B76A",
              }}
            >
              <img
                src={imageUrl}
                alt={getItemName()}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </Box>
          )}
          <Typography sx={{ fontWeight: 700, fontSize: 22 }}>
            Successfully Claimed!
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#6B7280", mt: 0.5 }}>
            Enjoy Your Subscription
          </Typography>
        </Box>

        {/* Plan Details Card */}
        <Card sx={{ borderRadius: 3, p: 2.5, mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#6B7280" }}>
              Plan Details
            </Typography>
            <Chip 
              label="Active" 
              size="small" 
              sx={{ bgcolor: "#DCFCE7", color: "#166534", fontSize: "11px", height: "22px" }} 
            />
          </Stack>

          <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 0.5 }}>
            {getItemName()}
          </Typography>

          <Typography sx={{ fontSize: 14, color: "#6B7280", mb: 2 }}>
            {getItemDescription()}
          </Typography>

          <Divider sx={{ my: 1.5 }} />

          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                Purchased With
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {getPurchasedWith()}
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                Valid Until
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {getValidUntil()}
              </Typography>
            </Box>
          </Stack>
        </Card>

        {/* Reward Unlocked Box */}
        {hasReward() && (
          <Card sx={{ borderRadius: 3, p: 2, mb: 3, bgcolor: "#EAF7F1", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: 16 }}>
                Reward Unlocked!
              </Typography>
              <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1.5 }}>
                You've Won Cashback On This Order
              </Typography>
              <Button
                variant="contained"
                size="small"
                sx={{
                  bgcolor: "#12B76A",
                  textTransform: "none",
                  borderRadius: 2,
                  px: 3,
                  "&:hover": { bgcolor: "#0EA861" },
                }}
                onClick={handleClaimReward}
              >
                Claim Now →
              </Button>
            </Box>
          </Card>
        )}

        {/* Go To Provider Button */}
        <Button
          fullWidth
          variant="contained"
          sx={{
            bgcolor: "#12B76A",
            borderRadius: 3,
            py: 1.6,
            fontWeight: 700,
            textTransform: "none",
            mb: 2,
            "&:hover": { bgcolor: "#0EA861" },
          }}
          onClick={handleGoToProvider}
        >
          {getButtonText()}
        </Button>

        {/* Home Button */}
        <Button
          fullWidth
          variant="outlined"
          sx={{
            borderRadius: 3,
            py: 1.6,
            fontWeight: 600,
            textTransform: "none",
            color: "#404237",
            borderColor: "#E5E7EB",
            "&:hover": {
              borderColor: "#14AE78",
              color: "#14AE78",
            },
          }}
          onClick={handleHome}
        >
          Home
        </Button>
      </Box>
    );
  }

  // ==================== FAILED/ERROR UI ====================
  return (
    <Box sx={{ maxWidth: 420, mx: "auto", minHeight: "100vh", bgcolor: "#F4F6F8", p: 2 }}>
      <Header />

      {/* Failed Message */}
      <Box textAlign="center" sx={{ mt: 2, mb: 3 }}>
        {getImageUrl() && (
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              overflow: "hidden",
              mx: "auto",
              mb: 2,
              border: "2px solid #EF4444",
              opacity: 0.5,
            }}
          >
            <img
              src={getImageUrl()}
              alt={getItemName()}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </Box>
        )}
        <Typography sx={{ fontWeight: 700, fontSize: 22, color: "#EF4444" }}>
          Claim Unsuccessful
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#6B7280", mt: 1 }}>
          Don't Worry – Your Payment Is Safe.
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#6B7280" }}>
          You'll Receive A Refund Within 24 Hours.
        </Typography>
      </Box>

      {/* Failed Section */}
      <Box 
        sx={{ 
          display: "flex", 
          flexDirection: "row", 
          justifyContent: "center", 
          alignItems: "center", 
          border: "1px solid #EF4444", 
          borderRadius: 2,
          px: 2,
          py: 1,
          mb: 3,
          bgcolor: "white"
        }}
      >
        <Typography 
          sx={{ 
            fontWeight: 500, 
            fontSize: 14, 
            color: "#EF4444",
            textAlign: "center",
            width: "100%"
          }}
        >
          Claim Failed • Please Try Again
        </Typography>
      </Box>

      {/* Plan Details Card */}
      <Card sx={{ borderRadius: 3, p: 2.5, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#6B7280" }}>
            Plan Details
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#EF4444", fontWeight: 600 }}>
            Failed
          </Typography>
        </Stack>

        <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 0.5 }}>
          {getItemName()}
        </Typography>

        <Typography sx={{ fontSize: 14, color: "#6B7280", mb: 2 }}>
          {getItemDescription()}
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        <Stack spacing={1.5}>
          <Box>
            <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
              Purchased With
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
              {getPurchasedWith()}
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
              Valid Until
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
              {getValidUntil()}
            </Typography>
          </Box>
        </Stack>
      </Card>

      {/* Help & Support Button */}
      <Button
        fullWidth
        variant="contained"
        sx={{
          bgcolor: "#EF4444",
          borderRadius: 3,
          py: 1.6,
          fontWeight: 700,
          textTransform: "none",
          mb: 2,
          "&:hover": { bgcolor: "#DC2626" },
        }}
        onClick={() => window.location.href = "mailto:support@daalchini.com"}
      >
        Help & Support
      </Button>

      {/* Go To Home Button */}
      <Button
        fullWidth
        variant="outlined"
        sx={{
          borderRadius: 3,
          py: 1.6,
          fontWeight: 600,
          textTransform: "none",
          color: "#404237",
          borderColor: "#E5E7EB",
          "&:hover": {
            borderColor: "#14AE78",
            color: "#14AE78",
          },
        }}
        onClick={handleHome}
      >
        Go To Home
      </Button>

      {/* View Orders Button */}
      <Button
        fullWidth
        variant="text"
        sx={{
          borderRadius: 3,
          py: 1.6,
          fontWeight: 600,
          textTransform: "none",
          color: "#6B7280",
          "&:hover": {
            color: "#EF4444",
            bgcolor: "#FEF2F2",
          },
        }}
        onClick={() => navigate("/orders")}
      >
        View Orders
      </Button>
    </Box>
  );
}